import React, { ReactNode, useRef, useEffect } from 'react';

interface ParallaxProps {
  children: ReactNode;
  offset?: number;
}

export function Parallax({ children, offset = 50 }: ParallaxProps) {
  const ref = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Check reduced-motion preference — skip parallax entirely if set
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const onScroll = () => {
      if (frameRef.current !== null) return; // already queued
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const windowH = window.innerHeight;
        // progress: 0 when bottom enters viewport, 1 when top leaves
        const progress = 1 - (rect.bottom / (windowH + rect.height));
        const y = (progress - 0.5) * offset * 2;
        el.style.transform = `translateY(${y}px)`;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // initial position
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [offset]);

  return (
    <div ref={ref}>
      {children}
    </div>
  );
}
